import React, { useEffect, useMemo, useState } from "react";
import {
  Card, CardBody, CardHeader, Col, Container, Row, Input, Label, Button, Table, Spinner, Badge,
} from "reactstrap";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import { getApiUsageStats, getApiCallList } from "../../helpers/fakebackend_helper";

const PAGE_SIZE = 20;

const serviceLabel = (service_id) =>
  String(service_id || "").toLowerCase().replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());

const fmtDateInput = (d) => d.toISOString().slice(0, 10);

const StatusBadge = ({ status }) => {
  switch (status) {
    case "COMPLETED":
      return <Badge color="success-subtle" className="bg-success-subtle text-success">Completed</Badge>;
    case "FAILED":
    case "ERROR":
      return <Badge color="danger-subtle" className="bg-danger-subtle text-danger">{status === "ERROR" ? "Error" : "Failed"}</Badge>;
    case "IN_PROCESSING":
      return <Badge color="warning-subtle" className="bg-warning-subtle text-warning">Processing</Badge>;
    case "IN_QUEUE":
      return <Badge color="info-subtle" className="bg-info-subtle text-info">Queued</Badge>;
    default:
      return <Badge color="secondary-subtle" className="bg-secondary-subtle text-secondary">{status}</Badge>;
  }
};

// Shared aggregate table for both groupings (by service / by api key).
const StatsTable = ({ rows, nameHeader, nameOf, loading, onRowClick, emptyText }) => {
  const totals = useMemo(() => rows.reduce((acc, r) => ({
    total: acc.total + r.total,
    success: acc.success + r.success,
    processing: acc.processing + r.processing,
    failed: acc.failed + r.failed,
    credits: acc.credits + r.credits,
  }), { total: 0, success: 0, processing: 0, failed: 0, credits: 0 }), [rows]);

  if (loading) {
    return <div className="text-center py-4"><Spinner color="primary" size="sm" /> <span className="text-muted ms-2">Loading...</span></div>;
  }
  if (rows.length === 0) {
    return <p className="text-muted text-center py-4 mb-0">{emptyText}</p>;
  }
  return (
    <div className="table-responsive table-card">
      <Table className="table align-middle table-nowrap mb-0">
        <thead className="table-light">
          <tr>
            <th>{nameHeader}</th>
            <th className="text-end">Total</th>
            <th className="text-end">Success</th>
            <th className="text-end">Processing</th>
            <th className="text-end">Failed</th>
            <th className="text-end">Success Rate</th>
            <th className="text-end">Credits</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const finished = r.success + r.failed;
            const rate = finished > 0 ? Math.round((r.success / finished) * 100) : null;
            return (
              <tr key={i} style={onRowClick ? { cursor: "pointer" } : undefined} onClick={onRowClick ? () => onRowClick(r) : undefined} title={onRowClick ? "Click to view requests" : undefined}>
                <td className="fw-medium">{nameOf(r)}</td>
                <td className="text-end">{r.total.toLocaleString("en-US")}</td>
                <td className="text-end text-success">{r.success.toLocaleString("en-US")}</td>
                <td className="text-end text-warning">{r.processing.toLocaleString("en-US")}</td>
                <td className="text-end text-danger">{r.failed.toLocaleString("en-US")}</td>
                <td className="text-end">
                  {rate === null ? <span className="text-muted">—</span> :
                    <span className={rate >= 90 ? "text-success" : rate >= 50 ? "text-warning" : "text-danger"}>{rate}%</span>}
                </td>
                <td className="text-end">{r.credits.toLocaleString("en-US")}</td>
              </tr>
            );
          })}
        </tbody>
        <tfoot className="table-light">
          <tr className="fw-semibold">
            <td>Total</td>
            <td className="text-end">{totals.total.toLocaleString("en-US")}</td>
            <td className="text-end">{totals.success.toLocaleString("en-US")}</td>
            <td className="text-end">{totals.processing.toLocaleString("en-US")}</td>
            <td className="text-end">{totals.failed.toLocaleString("en-US")}</td>
            <td className="text-end"></td>
            <td className="text-end">{totals.credits.toLocaleString("en-US")}</td>
          </tr>
        </tfoot>
      </Table>
    </div>
  );
};

const ApiUsage = () => {
  document.title = "API Usage | ApiHub.today";

  const defaultFrom = useMemo(() => fmtDateInput(new Date(Date.now() - 29 * 24 * 60 * 60 * 1000)), []);
  const defaultTo = useMemo(() => fmtDateInput(new Date()), []);
  const [from, setFrom] = useState(defaultFrom);
  const [to, setTo] = useState(defaultTo);

  const [stats, setStats] = useState({ by_service: [], by_key: [] });
  const [statsLoading, setStatsLoading] = useState(true);

  const [filterService, setFilterService] = useState("");
  const [filterKey, setFilterKey] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [page, setPage] = useState(1);
  const [calls, setCalls] = useState({ items: [], total: 0 });
  const [callsLoading, setCallsLoading] = useState(true);

  const loadStats = async () => {
    setStatsLoading(true);
    try {
      const params = {};
      if (from) params.from = from;
      if (to) params.to = to;
      const res = await getApiUsageStats(params);
      if (res && res.success) setStats(res.data || { by_service: [], by_key: [] });
    } catch (e) { /* handled by interceptor */ }
    finally { setStatsLoading(false); }
  };

  const loadCalls = async () => {
    setCallsLoading(true);
    try {
      const params = { page, page_size: PAGE_SIZE };
      if (from) params.from = from;
      if (to) params.to = to;
      if (filterService) params.service_id = filterService;
      if (filterKey) params.api_key_id = filterKey;
      if (filterStatus) params.status = filterStatus;
      const res = await getApiCallList(params);
      if (res && res.success) setCalls(res.data || { items: [], total: 0 });
    } catch (e) { /* handled by interceptor */ }
    finally { setCallsLoading(false); }
  };

  useEffect(() => { loadStats(); }, [from, to]);
  useEffect(() => { loadCalls(); }, [from, to, filterService, filterKey, filterStatus, page]);

  const totalPages = Math.max(1, Math.ceil(calls.total / PAGE_SIZE));

  const resetToPageOne = (setter) => (value) => { setter(value); setPage(1); };
  const setFilterServiceR = resetToPageOne(setFilterService);
  const setFilterKeyR = resetToPageOne(setFilterKey);
  const setFilterStatusR = resetToPageOne(setFilterStatus);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title="API Usage" pageTitle="Apps" />

          {/* Date range */}
          <Card>
            <CardBody className="py-3">
              <Row className="g-3 align-items-end">
                <Col sm="auto">
                  <Label className="form-label mb-1">From</Label>
                  <Input type="date" value={from} max={to || undefined} onChange={(e) => { setFrom(e.target.value); setPage(1); }} />
                </Col>
                <Col sm="auto">
                  <Label className="form-label mb-1">To</Label>
                  <Input type="date" value={to} min={from || undefined} onChange={(e) => { setTo(e.target.value); setPage(1); }} />
                </Col>
                <Col sm="auto">
                  <Button color="light" onClick={() => { setFrom(defaultFrom); setTo(defaultTo); setFilterService(""); setFilterKey(""); setFilterStatus(""); setPage(1); }}>
                    Reset
                  </Button>
                </Col>
              </Row>
            </CardBody>
          </Card>

          <Row>
            <Col xl="6">
              <Card>
                <CardHeader><h5 className="card-title mb-0">Requests by Service</h5></CardHeader>
                <CardBody>
                  <StatsTable
                    rows={stats.by_service}
                    nameHeader="Service"
                    nameOf={(r) => serviceLabel(r.service_id)}
                    loading={statsLoading}
                    emptyText="No requests in this period."
                    onRowClick={(r) => { setFilterServiceR(r.service_id); }}
                  />
                </CardBody>
              </Card>
            </Col>
            <Col xl="6">
              <Card>
                <CardHeader><h5 className="card-title mb-0">Requests by API Key</h5></CardHeader>
                <CardBody>
                  <StatsTable
                    rows={stats.by_key}
                    nameHeader="API Key"
                    nameOf={(r) => r.name}
                    loading={statsLoading}
                    emptyText="No requests in this period."
                    onRowClick={(r) => { setFilterKeyR(String(r.api_key_id)); }}
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* Request details */}
          <Card>
            <CardHeader className="d-flex flex-wrap gap-3 align-items-center">
              <h5 className="card-title mb-0 flex-grow-1">Request Details</h5>
              <div className="d-flex flex-wrap gap-2">
                <Input type="select" style={{ width: "auto" }} value={filterService} onChange={(e) => setFilterServiceR(e.target.value)}>
                  <option value="">All services</option>
                  {stats.by_service.map(r => (
                    <option key={r.service_id} value={r.service_id}>{serviceLabel(r.service_id)}</option>
                  ))}
                </Input>
                <Input type="select" style={{ width: "auto" }} value={filterKey} onChange={(e) => setFilterKeyR(e.target.value)}>
                  <option value="">All API keys</option>
                  {stats.by_key.map(r => (
                    <option key={r.api_key_id} value={r.api_key_id}>{r.name}</option>
                  ))}
                </Input>
                <Input type="select" style={{ width: "auto" }} value={filterStatus} onChange={(e) => setFilterStatusR(e.target.value)}>
                  <option value="">All statuses</option>
                  <option value="COMPLETED">Completed</option>
                  <option value="IN_PROCESSING">Processing</option>
                  <option value="IN_QUEUE">Queued</option>
                  <option value="FAILED">Failed</option>
                  <option value="ERROR">Error</option>
                  <option value="CANCELLED">Cancelled</option>
                </Input>
              </div>
            </CardHeader>
            <CardBody>
              {callsLoading ? (
                <div className="text-center py-4"><Spinner color="primary" size="sm" /> <span className="text-muted ms-2">Loading...</span></div>
              ) : calls.items.length === 0 ? (
                <p className="text-muted text-center py-4 mb-0">No requests match the current filters.</p>
              ) : (
                <div className="table-responsive table-card mb-3">
                  <Table className="table align-middle table-nowrap mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>Time</th>
                        <th>Service</th>
                        <th>API Key</th>
                        <th>Request ID</th>
                        <th>Status</th>
                        <th className="text-end">Credits</th>
                        <th className="text-end">Exec (ms)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {calls.items.map(item => (
                        <tr key={item.id}>
                          <td>{item.create_time ? new Date(item.create_time).toLocaleString() : ""}</td>
                          <td>{serviceLabel(item.service_id)}</td>
                          <td>{item.api_key_name}</td>
                          <td><code className="user-select-all">{item.request_id}</code></td>
                          <td><StatusBadge status={item.request_status} /></td>
                          <td className="text-end">{(item.credit || 0).toLocaleString("en-US")}</td>
                          <td className="text-end">{item.execution_time ? item.execution_time.toLocaleString("en-US") : "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
              <div className="d-flex justify-content-between align-items-center">
                <span className="text-muted">
                  Showing <b>{calls.items.length}</b> of <b>{calls.total.toLocaleString("en-US")}</b> requests
                </span>
                <div className="d-flex gap-2 align-items-center">
                  <Button color="light" disabled={page <= 1 || callsLoading} onClick={() => setPage(p => Math.max(1, p - 1))}>
                    Previous
                  </Button>
                  <span className="text-muted">Page {page} / {totalPages}</span>
                  <Button color="light" disabled={page >= totalPages || callsLoading} onClick={() => setPage(p => p + 1)}>
                    Next
                  </Button>
                </div>
              </div>
            </CardBody>
          </Card>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default ApiUsage;
